import * as React from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import colorSchemes from "../../../constants/colorSchemes";
import useStyles from "./SignIn.styles";
import Form from "react-bootstrap/Form";
import { useState, useContext } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import loginApi from "../../../api/loginAPI";
import { setToken, setUserData } from "../../../common";
import config from "../../../config/config";
import routes from "../../../constants/routes";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { TimerContext } from "../../../app/App";
import { sendToConfirmPageIfNeeded } from "../../../common/emailStatus";
import OtpInput from "react-otp-input";
import {
  requestSignInCodeAPI,
  signInWithCodeAPI,
} from "../../../api/signInCodeAPI";
import useResendCountdown, {
  RESEND_WAIT_SECONDS,
  formatCountdown,
} from "../../../common/useResendCountdown";

export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";

// Page to return to after login (set by the auction page login dialog).
// Only auction pages are allowed, so it can't be used to redirect elsewhere.
export const takePostLoginRedirect = () => {
  try {
    const path = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return path && /^\/auctions\/[\w-]+$/.test(path) ? path : null;
  } catch (error) {
    return null;
  }
};

// Email/password + social login form, shared by the Sign In page and the
// auction page login dialog. onSuccess runs after the session is stored.
// redirectAfterSocialLogin: hash path to come back to after Google/Facebook
// or after registering and signing in.
// registerPath: where "Create free account" leads (the account type choice
// by default; the auction dialog sends bidders straight to their form).
export default function LoginForm({
  onSuccess,
  redirectAfterSocialLogin,
  registerPath = routes.register,
}) {
  const timerContext = useContext(TimerContext);
  const classes = useStyles();

  const [loginInfo, setLoginInfo] = useState({
    email: "",
    password: "",
  });
  const [passwordType, setPasswordType] = useState("password");

  const handleChange = (event) => {
    setLoginInfo({ ...loginInfo, [event.target.name]: event.target.value });
  };

  const togglePassword = () => {
    if (passwordType === "password") {
      setPasswordType("text");
      return;
    }
    setPasswordType("password");
  };

  const showError = (error) => {
    if (error.response) {
      toast(error.response.data.error);
    } else if (error.request) {
      toast("Something went wrong!");
    } else {
      console.log("Error", error.message);
    }
    console.log(error);
  };

  const signedIn = (data) => {
    setToken(data.token); /* set user token */
    setUserData(data);
    timerContext.timerProps.activate();
    toast("Login Successfully!");
    // an unconfirmed email: only the confirm page until it is done
    if (sendToConfirmPageIfNeeded(data)) return;
    if (onSuccess) onSuccess(data);
  };

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();

      const { data } = await loginApi(loginInfo);
      signedIn(data);
    } catch (error) {
      showError(error);
      if (error.response?.status === 401) setShowSocialHint(true);
    }
  };

  // after a refused password: an account made with Google or Facebook has no
  // password (shown for every wrong password, so it reveals nothing)
  const [showSocialHint, setShowSocialHint] = useState(false);

  // "password": email + password; "codeEmail": email only, to ask for a
  // code; "codeEntry": the 6-digit code from the email.
  const [mode, setMode] = useState("password");
  const [code, setCode] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useResendCountdown();

  const sendCode = async (isResend) => {
    if (isSending) return;
    setIsSending(true);
    try {
      const { data } = await requestSignInCodeAPI({ email: loginInfo.email });
      toast(
        isResend
          ? "A new code has been sent. The old code no longer works."
          : data.message
      );
      setCode("");
      setMode("codeEntry");
      setResendSecondsLeft(RESEND_WAIT_SECONDS);
    } catch (error) {
      showError(error);
      // asked again too soon: the earlier code still works, so let them type it
      const waitSeconds = error.response?.data?.waitSeconds;
      if (waitSeconds) {
        setMode("codeEntry");
        setResendSecondsLeft(waitSeconds);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleCodeRequest = (event) => {
    event.preventDefault();
    sendCode(false);
  };

  const handleCodeSubmit = async (event) => {
    try {
      event.preventDefault();

      const { data } = await signInWithCodeAPI({
        email: loginInfo.email,
        code,
      });
      signedIn(data);
    } catch (error) {
      showError(error);
      setCode("");
    }
  };

  const formSubmit = {
    password: handleSubmit,
    codeEmail: handleCodeRequest,
    codeEntry: handleCodeSubmit,
  }[mode];

  const submitLabel = {
    password: "Login",
    codeEmail: "Email me a code",
    codeEntry: "Sign in",
  }[mode];

  const rememberRedirect = () => {
    if (!redirectAfterSocialLogin) return;
    try {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirectAfterSocialLogin);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <form layout="vertical" onSubmit={formSubmit}>
        {mode !== "codeEntry" && (
        <Box className={classes.forms}>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label className={classes.label}>E-mail</Form.Label>
            <Form.Control
              className={classes.input}
              type="email"
              placeholder="Enter email"
              name="email"
              value={loginInfo.email}
              onChange={handleChange}
              required={mode === "codeEmail"}
            />
          </Form.Group>
        </Box>
        )}
        {mode === "codeEmail" && (
          <Typography className={classes.forgot} sx={{ color: "#7E8184" }}>
            We'll email you a 6-digit code.
          </Typography>
        )}
        {mode === "codeEntry" && (
          <Box className={classes.forms}>
            <Typography className={classes.forgot} sx={{ color: "#7E8184" }}>
              We sent a code to <strong>{loginInfo.email}</strong>{" "}
              <Link
                to="#"
                onClick={(event) => {
                  event.preventDefault();
                  setMode("codeEmail");
                }}
              >
                (Change)
              </Link>
            </Typography>
            <Form.Group className="mb-3 otp" controlId="formSignInCode">
              <Form.Label className={classes.label}>Code</Form.Label>
              <OtpInput
                value={code}
                onChange={setCode}
                numInputs={6}
                inputType="tel"
                shouldAutoFocus
                renderSeparator={<span>-</span>}
                renderInput={(props) => <input {...props} />}
              />
            </Form.Group>
            <Typography className={classes.forgot}>
              {resendSecondsLeft > 0 ? (
                <span style={{ color: "#9A9DA0" }}>
                  Resend code in {formatCountdown(resendSecondsLeft)}
                </span>
              ) : (
                <Link
                  to="#"
                  onClick={(event) => {
                    event.preventDefault();
                    sendCode(true);
                  }}
                >
                  Resend code
                </Link>
              )}
            </Typography>
          </Box>
        )}
        {mode === "password" && (
        <Box className={classes.forms}>
          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label className={classes.label}>Password</Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Control
                className={classes.input}
                type={passwordType}
                name="password"
                placeholder="Enter Password"
                value={loginInfo.password}
                onChange={handleChange}
                style={{ paddingRight: "45px" }}
              />
              <button
                type="button"
                onClick={togglePassword}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: "5px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {passwordType === "password" ? (
                  <RemoveRedEyeIcon style={{ color: "#2D55EB" }} />
                ) : (
                  <VisibilityOffIcon style={{ color: "#2D55EB" }} />
                )}
              </button>
            </div>
          </Form.Group>
        </Box>
        )}

        {mode === "password" && (
        <Typography
          className={classes.forgot}
          sx={{ color: colorSchemes.primaryColor }}
        >
          <Link to="/forgot">Forgot Password ?</Link>
        </Typography>
        )}
        {mode === "password" && showSocialHint && (
          <Typography className={classes.forgot} sx={{ color: "#7E8184" }}>
            Signed up with Google or Facebook? Use that button below, or{" "}
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                setMode("codeEmail");
              }}
            >
              sign in with a code
            </Link>
            .
          </Typography>
        )}
        <Box>
          <Button
            sx={{
              backgroundColor: colorSchemes.primaryColor,
              color: "#fff",
              borderRadius: "0em",
              "&.MuiButtonBase-root:hover": {
                bgcolor: "#2D55EB !important",
              },
              "&.Mui-disabled": { color: "#fff", opacity: 0.5 },
            }}
            fullWidth
            type="submit"
            disabled={
              (mode === "codeEmail" && isSending) ||
              (mode === "codeEntry" && code.length !== 6)
            }
          >
            {submitLabel}
          </Button>
          <Typography
            className={classes.forgot}
            sx={{ textAlign: "center", color: colorSchemes.primaryColor }}
          >
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                setMode(mode === "password" ? "codeEmail" : "password");
              }}
            >
              {mode === "password"
                ? "Sign in with a code instead"
                : "Sign in with password instead"}
            </Link>
          </Typography>
          <a
            href={config.socialLogin.google}
            onClick={rememberRedirect}
            className="px-4 py-2 border flex gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400  transition duration-150 google-login"
          >
            <img
              className="w-6 h-6"
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              loading="lazy"
              alt="google logo"
            />
            <span>Continue with Google</span>
          </a>
          <a
            href={config.socialLogin.facebook}
            onClick={rememberRedirect}
            className="px-4 py-2 border flex gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400  transition duration-150 google-login"
          >
            <img
              className="w-6 h-6"
              src="https://www.svgrepo.com/show/448224/facebook.svg"
              loading="lazy"
              alt="facebook logo"
            />
            <span>Continue with Facebook</span>
          </a>
        </Box>
        <Box></Box>
      </form>
      <Typography className={classes.forgot}>
        Don't have an account?{" "}
        <Link to={registerPath} onClick={rememberRedirect}>
          Create free account
        </Link>{" "}
      </Typography>
    </>
  );
}
